// English resources (default locale on entry — SPEC §E). Keep keys in sync with pl.ts.
// User-facing render currency = "gems" (UI rebrand of internal `tokens`; code/DB unchanged).
// Public-storefront namespaces live in ./en/<surface>.ts (one per page); the app-shell
// keys stay inline below.
import { landing } from './en/landing'
import { pricing } from './en/pricing'
import { faq } from './en/faq'
import { auth } from './en/auth'
import { legal } from './en/legal'
// Logged-in app pages (one namespace per route).
import { appDashboard } from './en/appDashboard'
import { appSettings } from './en/appSettings'
import { appGenerator } from './en/appGenerator'
import { appBilling } from './en/appBilling'
import { appMusic } from './en/appMusic'
import { appAccounts } from './en/appAccounts'
import { appCampaigns } from './en/appCampaigns'
import { appCalendar } from './en/appCalendar'
import { appNotifications } from './en/appNotifications'
import { appStats } from './en/appStats'
import { appClipPacks } from './en/appClipPacks'
import { appBatches } from './en/appBatches'
import { appHooks } from './en/appHooks'
import { appCaptions } from './en/appCaptions'
import { appEditDetail } from './en/appEditDetail'
import { appLyrics } from './en/appLyrics'
import { appPackDetail } from './en/appPackDetail'
import { appPackQa } from './en/appPackQa'
import { appCollections } from './en/appCollections'
import { appStorage } from './en/appStorage'
import { appLyricsAdv } from './en/appLyricsAdv'
// Cross-cutting surfaces (not tied to one route).
import { dialogs } from './en/dialogs'
import { appComposer } from './en/appComposer'
import { tiktokPrivacy } from './en/tiktokPrivacy'
import { checkout } from './en/checkout'
import { errors } from './en/errors'
import { onboarding } from './en/onboarding'
import { series } from './en/series'

export const en = {
  common: {
    loading: 'Loading…',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    retry: 'Try again',
    // A server value the catalogue does not name (`tEnum`, paczka 12): a neutral word,
    // never the raw string and never a key path.
    unspecified: 'unspecified',
    // Favourites (rule 58): the star on a thing, the filter chip, the empty filter
    // result and the ONLY toast of this feature — success says nothing, the star shows it.
    favorite: 'Favourite',
    favorites: 'Favourites',
    favoritesEmpty: 'No favourites',
    showAll: 'Show all',
    favoriteError: 'Could not save favourites.',
    // Full-panel error state (`components/StateScreens.tsx`, seven routes). It lives in
    // `common` and NOT in `errors`: that namespace maps API error CODES to sentences and
    // its keys must be the server's literal codes. A screen state is not a code.
    // The button is `common.retry`, which already existed; no second copy.
    error: {
      title: 'We could not load this',
      body: 'The problem is on our side, not yours. Your edits and gems are safe - refresh in a moment.',
      backToDashboard: 'Back to edits',
    },
  },
  notFound: {
    title: 'Nothing here',
    body: 'This page is gone, moved, or never existed.',
    backToDashboard: 'Back to edits',
    library: 'Library',
    backToSite: 'Back to the home page',
  },
  lang: { label: 'Language', en: 'English', pl: 'Polski', switch: 'Change language', switchTo: 'Change language to {name}' },
  nav: {
    generator: 'Generator',
    // The sidebar entry existed in Layout.tsx from P03's merge with no key
    // behind it — and as an array literal, so a `t()` scan never saw it.
    // Production navigation showed the literal text "nav.batches".
    batches: 'Edits',
    // MINIMAL-2026-09-18 (MAPA 30, decision 4): the badge next to "Edits" counts renders
    // in flight and stays NEUTRAL (the number is a drawing, so this key carries it to a
    // screen reader); the "needs a hand" signal is a SEPARATE dot, never the badge colour.
    rendersInFlight: 'renders in progress: {count}',
    needsAttention: 'needs attention',
    library: 'All edits',
    sectionLibrary: 'Library',
    music: 'Music',
    clipPacks: 'Clips',
    hooks: 'Hooks',
    // Third instance of the mechanism described at `batches` above: a sidebar
    // entry living as an array literal in appNav.ts, invisible to a `t()` scan.
    // Caught by P04's i18n-keys-complete gate, which scans for key-shaped
    // literals anywhere.
    captions: 'Captions',
    // Package L (rule 43): group labels in the Library tab strip. CSS draws the capitals
    // (`.mn-tabs-glabel`), so a screen reader gets the word, not a spelled-out acronym.
    groupGenerator: 'Generator',
    groupPublishing: 'Publishing',
    // Publishing (sesja 12 + 22, MINIMAL-2026-09-18): a navigation ITEM for an account
    // with access (`GET /api/campaigns` → `enabled`); its tabs are Planner · Calendar ·
    // Your accounts · Captions.
    sectionDistribution: 'Publishing',
    // The plan's dictionary says "Planner" — not "Autopilot" and not "Publishing planner":
    // the tab and the screen title carry the same word (plan, rule 7).
    campaigns: 'Planner',
    calendar: 'Calendar',
    // Session 35: the tab is called „Accounts”. The screen title is „Publishing”, so
    // „Your” has nothing left to distinguish, and the shorter label fits five tabs on 390.
    accounts: 'Accounts',
    // The fifth Publishing tab (session 36); shown only when the feature really exists.
    stats: 'Stats',
    sectionAccount: 'Account',
    billing: 'Tokens & plan',
    settings: 'Settings',
    // Accessible name of the 390 menu button (rule 29). `more` DIED with the bottom
    // bar and the "More" sheet (R5): the drawer carries the whole panel.
    menu: 'Menu',
    // Shell balance after R3 + rule 34: the number and "Top up" stay visible. There is
    // no SENTENCE under the balance — the state is the colour of the number, and paused
    // sales are an inactive "Top up". The reader gets the state NAME, nothing more.
    topup: 'Top up',
    gemsLow: 'low balance',
    gemsZero: 'no tokens',
    gemsNoPay: 'sales paused',
    loadingRoute: 'Loading…',
  },
  topbar: { gems: 'Gems', tokens: 'Tokens', topup: 'Buy gems', planAndBalance: '{plan} · gems: {balance}', renewal: 'renews {date}' },
  userMenu: { language: 'Language', logout: 'Sign out' },
  pending: {
    rejectedTitle: 'Your application was not approved',
    rejectedBody: 'Your account hasn’t been activated. If this is a mistake, get in touch.',
    logout: 'Sign out',
  },
  dashboard: {
    topup: 'Buy gems',
    low: 'low',
    available: 'available',
  },
  settings: {
    languageRegion: 'Language & region',
    language: 'Language',
  },
  support: {
    button: 'Support',
    title: 'Support',
    subtitle: 'We usually reply within a day.',
    intro: 'Have a question? Write to us - we’re here to help.',
    placeholder: 'Write a message',
    send: 'Send',
    you: 'You',
    team: 'EDIMI',
    sending: 'Sending',
    sendFailed: 'We could not send your message. Try again.',
    close: 'Close',
  },
  cookie: {
    aria: 'Cookie notice',
    title: 'Cookies',
    body: 'We use only the cookies needed for signing in and paying - without asking for consent, because none of them requires it. Details in our',
    policy: 'Privacy Policy',
    // Paczka 37 (artboard 01): one line, one sentence, the link is the document name.
    // The older keys stay — they come back with analytics and two buttons.
    bodyMinimal: 'We use only the cookies needed for signing in and paying. No consent asked, because none of them requires it.',
    policyMinimal: 'Privacy Policy',
    dismiss: 'Got it',
  },
  landing,
  pricing,
  faq,
  auth,
  legal,
  appDashboard,
  appSettings,
  appGenerator,
  appBilling,
  appMusic,
  appAccounts,
  appCampaigns,
  appCalendar,
  appNotifications,
  appStats,
  appClipPacks,
  appBatches,
  appHooks,
  appCaptions,
  appEditDetail,
  appLyrics,
  appPackDetail,
  appPackQa,
  appCollections,
  appStorage,
  appLyricsAdv,
  dialogs,
  appComposer,
  tiktokPrivacy,
  checkout,
  errors,
  onboarding,
  series,
}

export type Resources = typeof en
